import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2, Save, Loader2, FileSpreadsheet } from 'lucide-react';
import { accountingAPI, agencyAPI, carrierAPI } from '@/lib/api';
import { toast } from 'sonner';

/**
 * Per-agency Medicare commission schedule (override grid).
 * KR pays a fixed $ per effectuated Medicare enrollment, by carrier, split
 * into New (== Plan Change) and Renewal. Auto-fills Amount To Pay at
 * reconciliation; editable per line.
 */
export default function CommissionSchedulesPage() {
  const [agencies, setAgencies] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [agencyId, setAgencyId] = useState('');
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addCarrierId, setAddCarrierId] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [ag, ca] = await Promise.all([agencyAPI.list(), carrierAPI.list()]);
        setAgencies(ag.data || []);
        setCarriers(ca.data || []);
      } catch { /* noop */ }
    })();
  }, []);

  const loadSchedule = useCallback(async (aid) => {
    if (!aid) { setRates([]); return; }
    setLoading(true);
    try {
      const { data } = await accountingAPI.getAgencySchedule(aid);
      setRates((data.medicare_rates || []).map((r) => ({ ...r })));
    } catch { setRates([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadSchedule(agencyId); }, [agencyId, loadSchedule]);

  const carrierName = (cid) => carriers.find((c) => c.carrier_id === cid)?.carrier_name || cid;

  const availableCarriers = useMemo(
    () => carriers.filter((c) => !rates.some((r) => r.carrier_id === c.carrier_id)),
    [carriers, rates],
  );

  const addRate = () => {
    if (!addCarrierId) return;
    setRates((prev) => [...prev, {
      carrier_id: addCarrierId,
      carrier_name: carrierName(addCarrierId),
      new_amount: '', renewal_amount: '',
    }]);
    setAddCarrierId('');
  };

  const updateRate = (cid, field, value) => {
    setRates((prev) => prev.map((r) => (r.carrier_id === cid ? { ...r, [field]: value } : r)));
  };
  const removeRate = (cid) => setRates((prev) => prev.filter((r) => r.carrier_id !== cid));

  const save = async () => {
    if (!agencyId) { toast.error('Pick an agency first'); return; }
    setSaving(true);
    try {
      await accountingAPI.saveAgencySchedule(agencyId, { medicare_rates: rates });
      toast.success('Commission schedule saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save schedule');
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-5" data-testid="commission-schedules-page">
      <div>
        <h1 className="text-2xl font-heading font-semibold flex items-center gap-2">
          <FileSpreadsheet size={22} /> Commission Schedules
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Fixed Medicare override paid to each agency per enrollment. Auto-fills
          "Amount To Pay" when a statement is reconciled — always editable per line.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Agency</CardTitle>
        </CardHeader>
        <CardContent>
          <Select value={agencyId} onValueChange={setAgencyId}>
            <SelectTrigger className="max-w-md" data-testid="schedule-agency-select">
              <SelectValue placeholder="Select an agency…" />
            </SelectTrigger>
            <SelectContent>
              {agencies.map((a) => (
                <SelectItem key={a.agency_id} value={a.agency_id}>{a.agency_name || a.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {agencyId && (
        <Card data-testid="schedule-grid-card">
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-base">Medicare override per carrier</CardTitle>
            <Button size="sm" onClick={save} disabled={saving} data-testid="save-schedule-btn">
              {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />}
              Save schedule
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" /></div>
            ) : (
              <>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 border-b">
                      <th className="py-2">Carrier</th>
                      <th className="py-2 w-40">New / Plan Change ($)</th>
                      <th className="py-2 w-40">Renewal ($ / yr)</th>
                      <th className="py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rates.length === 0 ? (
                      <tr><td colSpan={4} className="py-6 text-center text-slate-400">No carriers yet — add one below.</td></tr>
                    ) : rates.map((r) => (
                      <tr key={r.carrier_id} className="border-b border-slate-100" data-testid={`rate-row-${r.carrier_id}`}>
                        <td className="py-2 pr-2 font-medium">{carrierName(r.carrier_id)}</td>
                        <td className="py-2 pr-2">
                          <Input type="number" step="0.01" value={r.new_amount ?? ''}
                            onChange={(e) => updateRate(r.carrier_id, 'new_amount', e.target.value)}
                            placeholder="80.00" data-testid={`rate-new-${r.carrier_id}`} />
                        </td>
                        <td className="py-2 pr-2">
                          <Input type="number" step="0.01" value={r.renewal_amount ?? ''}
                            onChange={(e) => updateRate(r.carrier_id, 'renewal_amount', e.target.value)}
                            placeholder="30.00" data-testid={`rate-renewal-${r.carrier_id}`} />
                        </td>
                        <td className="py-2">
                          <Button size="icon" variant="ghost" onClick={() => removeRate(r.carrier_id)}
                            data-testid={`rate-remove-${r.carrier_id}`}>
                            <Trash2 size={15} className="text-red-500" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="mt-4 flex items-center gap-2">
                  <Select value={addCarrierId} onValueChange={setAddCarrierId}>
                    <SelectTrigger className="max-w-xs" data-testid="add-carrier-select">
                      <SelectValue placeholder="Add a carrier…" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableCarriers.map((c) => (
                        <SelectItem key={c.carrier_id} value={c.carrier_id}>{c.carrier_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={addRate} disabled={!addCarrierId} data-testid="add-carrier-btn">
                    <Plus size={14} className="mr-1" /> Add
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
